<template>
  <div v-bind='$attrs'>
    <label
      v-if='label'
      :for='name'
      class='block gap-0.5 truncate text-sm font-normal'
      :class='[
        (disabled && editMode) ? "text-gray-400" : "text-gray-700",
        editMode ? "w-full max-w-[50%]" : "",
      ]'
    >
      <QuestionMarkCircleIcon
        v-if='help'
        class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
        @click='setHelpModalVisibility(true)'
      />
      <span
        :title='label'
        v-html='getLabel()'
      />
    </label>
    <template v-if='editMode'>
      <div class='group relative mt-1'>
        <div
          v-if='withIcon'
          class='pointer-events-none absolute inset-y-0 left-3 flex items-center py-3'
        >
          <component
            :is='icon'
            class='shrink-0'
            :class='[ isFilterField ? "h-4 w-4" : "h-6 w-6" ]'
            aria-hidden='true'
          />
        </div>
        <input
          :id='name'
          data-cy='string-field-input'
          :value='fieldValue'
          :name='name'
          :type='type'
          :placeholder='placeholder'
          :aria-describedby='ariaDescribedBy'
          :disabled='disabled'
          class='relative z-0 block rounded-md text-sm shadow-sm'
          :class='[
            error ? "border-red-300 focus:ring-red-500 focus:border-red-500 caret-red-500 selection:bg-red-300 selection:text-red-900" : "border-gray-300 focus:ring-gray-500 focus:border-gray-500",
            disabled ? "bg-gray-50 text-gray-400" : "",
            withIcon ? "pl-11" : "",
            inputClasses,
            isFilterField ? "pr-11 h-[1.6rem]" : "",
            (isFilterField && fieldValue) ? "w-full min-w-[100px]" : "w-full",
          ]'
          @input='updateValue($event.target.value); trimValue(field, $event.target.value);'
          @focus='$emit("has-focus", name)'
        >
        <div
          v-if='isFilterField'
          class='absolute inset-y-0 right-3 z-10 flex h-0 w-0 cursor-pointer items-center opacity-0 transition-opacity group-hover:h-auto group-hover:w-auto group-hover:opacity-100'
          title='Filter empty values'
          @click='filterEmptyValues'
        >
          <FilterIcon
            :is='icon'
            class='h-4 w-4 shrink-0 opacity-0 group-hover:opacity-100'
            aria-hidden='true'
          />
        </div>
        <InputError :error='error'/>
      </div>
    </template>
    <template v-else>
      <div class='inline-flex items-center gap-2 truncate text-sm'>
        <span
          data-cy='preview-value'
          :title='displayValue'
        >
          {{ displayValue }}
        </span>
        <slot name='fieldValue'/>
        <span
          v-if='field.type === "color"'
          class='ml-4 rounded-full px-4 text-xs shadow-sm'
          :style='{ background: displayValue }'
        >
          &nbsp;
        </span>
      </div>

    </template>
  </div>
  <Modal
    :open='helpVisible'
    :title='label'
    @close-modal='setHelpModalVisibility(false)'
  >
    <template #content>
      <p v-html='help'/>
    </template>
  </Modal>
</template>

<script lang="ts">
  import { computed, defineComponent } from 'vue'
  import useInputFieldBase, { InputFieldProps,
                              InputFieldComponents,
                              InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps,
                                 InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'
  import { FilterIcon } from '@heroicons/vue/outline'

  export default defineComponent({
    name: 'StringField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      FilterIcon,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      type: {
        type: String,
        default: 'text',
      },
      withIcon: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
        default: null,
      },
    },
    emits: [
      ...InputFieldEmits,
      'has-focus',
    ],
    setup(props) {
      const { helpVisible, setHelpModalVisibility } = useInputFieldHasHelp()
      const { updateValue, trimValue, getLabel } = useInputFieldBase()

      const fieldValue = computed(() => {
        if (props.modelValue === '__EMPTY__') return 'Empty'
        if ((props.field.type === 'money' || props.field.type === 'quantity') && !props.isFilterField) {
          if (props.modelValue === 0) return '0'

          let value = formatValue(props.modelValue)

          value = !value ? props.modelValue : value

          return value
        }

        return props.modelValue
      })

      function formatValue(input: number|string) {
        let str = String(input)?.trim()

        if (str.endsWith(',')) {
          str = str.slice(0, -1) + '.'
        }

        const [whole, decimal] = str.split('.')
        const formattedWhole = new Intl.NumberFormat('en-US')
          .format(whole)
          .replace(/,/g, ' ')

        if(formattedWhole === 'NaN') {
          return null
        }

        return decimal !== undefined ? `${formattedWhole}.${decimal}` : formattedWhole
      }

      const displayValue = computed(() => {
        if((props.field.type !== 'money' && props.field.type !== 'quantity' && props.field.type !== 'number') || props.isFilterField || !['number', 'string'].includes(typeof fieldValue.value)) {
          return fieldValue.value
        }

        const value = String(fieldValue.value).trim()

        if (value === null) {
          return value
        }

        const formatedValue = new Intl.NumberFormat('en-US', {
          useGrouping: true, maximumFractionDigits: 8,
        }).format(value)

        if (formatedValue === 'NaN') {
          return value
        }

        return formatedValue.replace(/,/g, ' ')
      })

      const filterEmptyValues = () => {
        if (props.modelValue === '__EMPTY__') updateValue('')
        else updateValue('__EMPTY__')
      }

      return {
        updateValue,
        trimValue,
        getLabel,
        helpVisible,
        setHelpModalVisibility,
        fieldValue,
        displayValue,
        filterEmptyValues,
      }
    },
  })
</script>
